import React from 'react';
import { Paper, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { MonitorTargetType, MissingDataTreatment } from '@moderepo/mode-apis';
import { useModePanelStyle, useModeFormStyle, BaseInfoCompProps, BaseEntityField, InfoCompPanelHeader, ModeInfoCompTextInputField }
    from '@moderepo/mode-app-components';



const useStyle = makeStyles((theme: Theme) => {
    return {
        actionsSection: {
            border              : `1px solid ${theme.palette.divider}`,
            borderRadius        : 4,
            '&:not(:last-child)': {
                marginBottom: theme.spacing(2),
            },
        },

        actionsSectionTitle: {
            fontWeight: 500,
        },

        actionsContainer: {
            margin                      : theme.spacing(1, 0, 1, 2),
            [theme.breakpoints.up('sm')]: {
                margin: theme.spacing(2, 0, 2, 2),
            },
        },
    };
});


interface RCMonitorInfoProps extends BaseInfoCompProps {
    readonly fields: {
        readonly id: BaseEntityField<number>;
        readonly status: BaseEntityField<boolean>;
        readonly name: BaseEntityField<string>;
        readonly description: BaseEntityField<string>;
        readonly severity: BaseEntityField<number>;
        readonly target: BaseEntityField<MonitorTargetType>;
        readonly targetHomeId: BaseEntityField<number>;
        readonly conditions: BaseEntityField<string>;
        readonly treatMissingDataAs: BaseEntityField<MissingDataTreatment>;
        readonly sampleInterval: BaseEntityField<number>;
        readonly createIssueAction?: {
            readonly description: BaseEntityField<string>;
            readonly autoResolveIssue: BaseEntityField<string>;
        };
        readonly sendEmailAction?: {
            readonly email: BaseEntityField<string>;
            readonly subject: BaseEntityField<string>;
            readonly body: BaseEntityField<string>;
            readonly recoverBody: BaseEntityField<string>;
        };
        readonly createTicketAction?: {
            readonly systemType: BaseEntityField<string>;
            readonly parameters: BaseEntityField<string>;
        };
        readonly slackMessageAction?: {
            readonly channelId: BaseEntityField<string>;
            readonly message: BaseEntityField<string>;
            readonly iconEmoji: BaseEntityField<string>;
            readonly username: BaseEntityField<string>;
            readonly recoverMessage: BaseEntityField<string>;
            readonly recoverIconEmoji: BaseEntityField<string>;
            readonly recoverUsername: BaseEntityField<string>;
        };
        readonly updatedAgentId: BaseEntityField<string>;
        readonly updatedTime: BaseEntityField<string>;
        readonly createdAgentId: BaseEntityField<string>;
        readonly creationTime: BaseEntityField<string>;
    }

    readonly texts: {
        readonly monitorTriggerTitle: string;
        readonly monitorActionsTitle: string;
        readonly createIssueActionTitle: string;
        readonly sendEmailActionTitle: string;
        readonly createTicketActionTitle: string;
        readonly slackMessageActionTitle: string;
    }
}


/**
 * This is a DUMB component used for displaying Monitor info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const RCMonitorInfo = (props: RCMonitorInfoProps) => {

    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form className={clsx(formClasses.root, props.formProps?.className)} noValidate autoComplete="on" method="POST">
                    <div className={formClasses.formInputGroup}>
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.id}
                            fieldName="id"
                            inputValue={props.fields.id.displayValue ?? props.fields.id.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.name}
                            fieldName="name"
                            inputValue={props.fields.name.displayValue ?? props.fields.name.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            multiline
                            compProps={props}
                            inputProps={props.fields.description}
                            fieldName="description"
                            inputValue={props.fields.description.displayValue ?? props.fields.description.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.severity}
                            fieldName="severity"
                            inputValue={props.fields.severity.displayValue ?? props.fields.severity.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.target}
                            fieldName="target"
                            inputValue={props.fields.target.displayValue ?? props.fields.target.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.targetHomeId}
                            fieldName="targetHomeId"
                            inputValue={props.fields.targetHomeId.displayValue ?? props.fields.targetHomeId.value}
                        />

                        <div className={formClasses.formInputSection}>
                            <div className={formClasses.formInputSectionTitle}>
                                {props.texts.monitorTriggerTitle}
                            </div>
                            <div className={formClasses.formInputSectionContent}>
                                <div className={formClasses.formInputGroup}>
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        multiline
                                        compProps={props}
                                        inputProps={props.fields.conditions}
                                        fieldName="conditions"
                                        inputValue={props.fields.conditions.displayValue ?? props.fields.conditions.value}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.sampleInterval}
                                        fieldName="sampleInterval"
                                        inputValue={props.fields.sampleInterval.displayValue ?? props.fields.sampleInterval.value}
                                    />
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.treatMissingDataAs}
                                        fieldName="treatMissingDataAs"
                                        inputValue={props.fields.treatMissingDataAs.displayValue ?? props.fields.treatMissingDataAs.value}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className={formClasses.formInputSection}>
                            <div className={formClasses.formInputSectionTitle}>
                                {props.texts.monitorActionsTitle}
                            </div>
                            <div className={formClasses.formInputSectionContent}>
                                <div className={formClasses.formInputGroup}>
                                    {props.fields.createIssueAction && (
                                        <fieldset className={classes.actionsSection}>
                                            <legend className="legend">{props.texts.createIssueActionTitle}</legend>
                                            <div className={classes.actionsContainer}>
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    multiline
                                                    compProps={props}
                                                    inputProps={props.fields.createIssueAction.description}
                                                    fieldName="createIssueAction.description"
                                                    inputValue={props.fields.createIssueAction.description.displayValue
                                                        ?? props.fields.createIssueAction.description.value}
                                                />
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    compProps={props}
                                                    inputProps={props.fields.createIssueAction.autoResolveIssue}
                                                    fieldName="createIssueAction.autoResolveIssue"
                                                    inputValue={props.fields.createIssueAction.autoResolveIssue.displayValue
                                                             ?? props.fields.createIssueAction.autoResolveIssue.value}
                                                />
                                            </div>
                                        </fieldset>
                                    )}

                                    {props.fields.sendEmailAction && (
                                        <fieldset className={classes.actionsSection}>
                                            <legend className="legend">{props.texts.sendEmailActionTitle}</legend>
                                            <div className={classes.actionsContainer}>
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="email"
                                                    compProps={props}
                                                    inputProps={props.fields.sendEmailAction.email}
                                                    fieldName="sendEmailAction.email"
                                                    inputValue={props.fields.sendEmailAction.email.displayValue
                                                        ?? props.fields.sendEmailAction.email.value}
                                                />
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    multiline
                                                    compProps={props}
                                                    inputProps={props.fields.sendEmailAction.body}
                                                    fieldName="sendEmailAction.body"
                                                    inputValue={props.fields.sendEmailAction.body.displayValue
                                                        ?? props.fields.sendEmailAction.body.value}
                                                />
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    multiline
                                                    compProps={props}
                                                    inputProps={props.fields.sendEmailAction.recoverBody}
                                                    fieldName="sendEmailAction.recoverBody"
                                                    inputValue={props.fields.sendEmailAction.recoverBody.displayValue
                                                         ?? props.fields.sendEmailAction.recoverBody.value}
                                                />
                                            </div>
                                        </fieldset>
                                    )}

                                    {props.fields.createTicketAction && (
                                        <fieldset className={classes.actionsSection}>
                                            <legend className="legend">{props.texts.createTicketActionTitle}</legend>
                                            <div className={classes.actionsContainer}>
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    compProps={props}
                                                    inputProps={props.fields.createTicketAction.systemType}
                                                    fieldName="createTicketAction.systemType"
                                                    inputValue={props.fields.createTicketAction.systemType.displayValue
                                                        ?? props.fields.createTicketAction.systemType.value}
                                                />
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    multiline
                                                    compProps={props}
                                                    inputProps={props.fields.createTicketAction.parameters}
                                                    fieldName="createTicketAction.parameters"
                                                    inputValue={props.fields.createTicketAction.parameters.displayValue
                                                        ?? props.fields.createTicketAction.parameters.value}
                                                />
                                            </div>
                                        </fieldset>
                                    )}

                                    {props.fields.slackMessageAction && (
                                        <fieldset className={classes.actionsSection}>
                                            <legend className="legend">{props.texts.slackMessageActionTitle}</legend>
                                            <div className={classes.actionsContainer}>
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    compProps={props}
                                                    inputProps={props.fields.slackMessageAction.channelId}
                                                    fieldName="slackMessageAction.channelId"
                                                    inputValue={props.fields.slackMessageAction.channelId.displayValue
                                                        ?? props.fields.slackMessageAction.channelId.value}
                                                />
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    multiline
                                                    compProps={props}
                                                    inputProps={props.fields.slackMessageAction.message}
                                                    fieldName="slackMessageAction.message"
                                                    inputValue={props.fields.slackMessageAction.message.displayValue
                                                         ?? props.fields.slackMessageAction.message.value}
                                                />
                                                <div className={clsx(formClasses.formInputGroup, 'x-small')}>
                                                    <ModeInfoCompTextInputField
                                                        className={formClasses.formInputField}
                                                        inputType="text"
                                                        compProps={props}
                                                        inputProps={props.fields.slackMessageAction.iconEmoji}
                                                        fieldName="slackMessageAction.iconEmoji"
                                                        inputValue={props.fields.slackMessageAction.iconEmoji.displayValue
                                                            ?? props.fields.slackMessageAction.iconEmoji.value}
                                                    />
                                                    <ModeInfoCompTextInputField
                                                        className={formClasses.formInputField}
                                                        inputType="text"
                                                        compProps={props}
                                                        inputProps={props.fields.slackMessageAction.username}
                                                        fieldName="slackMessageAction.username"
                                                        inputValue={props.fields.slackMessageAction.username.displayValue
                                                            ?? props.fields.slackMessageAction.username.value}
                                                    />
                                                </div>
                                                <ModeInfoCompTextInputField
                                                    className={formClasses.formInputField}
                                                    inputType="text"
                                                    multiline
                                                    compProps={props}
                                                    inputProps={props.fields.slackMessageAction.recoverMessage}
                                                    fieldName="slackMessageAction.recoverMessage"
                                                    inputValue={props.fields.slackMessageAction.recoverMessage.displayValue
                                                        ?? props.fields.slackMessageAction.recoverMessage.value}
                                                />
                                                <div className={clsx(formClasses.formInputGroup, 'x-small')}>
                                                    <ModeInfoCompTextInputField
                                                        className={formClasses.formInputField}
                                                        inputType="text"
                                                        compProps={props}
                                                        inputProps={props.fields.slackMessageAction.recoverIconEmoji}
                                                        fieldName="slackMessageAction.recoverIconEmoji"
                                                        inputValue={props.fields.slackMessageAction.recoverIconEmoji.displayValue
                                                            ?? props.fields.slackMessageAction.recoverIconEmoji.value}
                                                    />
                                                    <ModeInfoCompTextInputField
                                                        className={formClasses.formInputField}
                                                        inputType="text"
                                                        compProps={props}
                                                        inputProps={props.fields.slackMessageAction.recoverUsername}
                                                        fieldName="slackMessageAction.recoverUsername"
                                                        inputValue={props.fields.slackMessageAction.recoverUsername.displayValue
                                                            ?? props.fields.slackMessageAction.recoverUsername.value}
                                                    />
                                                </div>
                                            </div>
                                        </fieldset>
                                    )}
                                </div>
                            </div>
                        </div>

                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.status}
                            fieldName="status"
                            inputValue={props.fields.status.displayValue ?? props.fields.status.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.createdAgentId}
                            fieldName="createdAgentId"
                            inputValue={props.fields.createdAgentId.displayValue ?? props.fields.createdAgentId.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.creationTime}
                            fieldName="creationTime"
                            inputValue={props.fields.creationTime.displayValue ?? props.fields.creationTime.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.updatedAgentId}
                            fieldName="updatedAgentId"
                            inputValue={props.fields.updatedAgentId.displayValue ?? props.fields.updatedAgentId.value}
                        />
                        <ModeInfoCompTextInputField
                            className={formClasses.formInputField}
                            inputType="text"
                            compProps={props}
                            inputProps={props.fields.updatedTime}
                            fieldName="updatedTime"
                            inputValue={props.fields.updatedTime.displayValue ?? props.fields.updatedTime.value}
                        />
                    </div>
                </form>
            </div>

        </Paper>
    );

};
